import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/constants";
import { useLang } from "@/i18n";
import { RowSearch } from "@/pages/shared";
import { addDays } from "@/components/dashboard/planning/planningConstants";

export default function PlanningQuickDialog({ quick, onClose, mode, setMode, nights, setNights,
  gq, setGq, visibleGuests, newGuest, setNewGuest, createBooking, busy }) {
  const { t } = useLang();
  return (
    <Dialog open={!!quick} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="planning-quick-dialog">
        <DialogHeader><DialogTitle className="font-display">{mode === "checked_in" ? t("quick_booking_now") : t("quick_booking")} — {quick?.pitch} · {quick && fmtDate(quick.day)}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2" data-testid="quick-mode">
            {[["reserved", t("quick_mode_reserved"), "#f59e0b"], ["checked_in", t("quick_mode_booking"), "#e11d48"]].map(([k, label, color]) => (
              <button key={k} onClick={() => setMode(k)} data-testid={`quick-mode-${k}`}
                className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm text-left transition-all ${mode === k ? "border-transparent shadow-md" : "border-border hover:bg-muted"}`}
                style={mode === k ? { background: `${color}18`, boxShadow: `inset 0 0 0 2px ${color}` } : {}}>
                <span className="h-3.5 w-3.5 rounded-[4px] shrink-0" style={{ background: color, boxShadow: "inset 0 1px 0 rgba(255,255,255,.6)" }} />
                <span className="font-semibold text-foreground">{label}</span>
              </button>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <div className="space-y-1 w-28">
              <Label className="text-xs">{t("nights")}</Label>
              <Input type="number" min="1" max={quick?.maxNights} value={nights}
                onChange={(e) => setNights(Math.max(1, Math.min(Number(e.target.value) || 1, quick?.maxNights || 999)))}
                data-testid="quick-nights" />
            </div>
            <div className="pb-2 space-y-0.5">
              <p className="text-xs font-semibold text-foreground/90">{quick && `${fmtDate(quick.day)} → ${fmtDate(addDays(quick.day, Math.max(1, Number(nights) || 1)))}`}</p>
              {quick?.maxNights < 999 && (
                <p className="text-[11px] text-amber-700" data-testid="quick-max-nights">
                  {t("quick_max_nights")}: {quick.maxNights} — {t("quick_free_until")} {fmtDate(addDays(quick.day, quick.maxNights))}
                </p>
              )}
            </div>
          </div>
          <RowSearch value={gq} onChange={setGq} placeholder={t("search_guest")} testid="quick-guest-search" />
          <div className="max-h-52 overflow-auto cb-scroll rounded-lg border border-border divide-y divide-slate-100">
            {visibleGuests.map((g) => (
              <button key={g.id} onClick={() => createBooking(g.id)} disabled={busy} data-testid={`quick-guest-${g.id}`}
                className="w-full text-left px-3 py-2 hover:bg-muted disabled:opacity-50">
                <span className="block text-sm font-semibold text-foreground">{g.last_name} {g.first_name}</span>
                <span className="block text-[11px] text-muted-foreground">{g.email || g.country || ""}</span>
              </button>
            ))}
          </div>
          <div className="rounded-lg bg-muted/50 p-3 space-y-2">
            <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{t("new_guest")}</p>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder={t("first_name")} value={newGuest.first_name} onChange={(e) => setNewGuest({ ...newGuest, first_name: e.target.value })} data-testid="quick-first-name" />
              <Input placeholder={t("last_name")} value={newGuest.last_name} onChange={(e) => setNewGuest({ ...newGuest, last_name: e.target.value })} data-testid="quick-last-name" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button onClick={() => createBooking(null)} disabled={busy || !newGuest.first_name || !newGuest.last_name} data-testid="quick-create-guest-booking">{mode === "checked_in" ? t("quick_mode_booking") : t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
